/**
 * React hook for managing Auriga Deck-related subscription data.
 * Subscribes to real-time deck lock/release, and update events using AppSync.
 */

import { onLockDeck, onReleaseDeckLock, onUpdateDeck } from '@services/api/deckManagement/graphql/schema/subscriptions';

import { decksQueryKeys } from './useAllDecks';
import { deckQueryKeys } from './useDeck';
import { createSubscriptionHook, SubscriptionHookName } from './useSubscriptionFactory';

/**
 * Interface for Deck Subscription parameters
 * @param deckId - The unique identifier for the deck to subscribe to deck events
 * @param enabled - Whether the subscription should be made active
 * @interface
 */
export interface UseDeckSubscriptionParams {
  deckId: string;
  enabled?: boolean;
}

/**
 * Invalidates deck-specific and deck list queries for a given deckId.
 */
const getDeckInvalidateKeys = (deckId: string) => [deckQueryKeys.deck(deckId), decksQueryKeys.list()];

/**
 * Creates a subscription hook for Deck events (lock, release, update).
 */
const deckSubscriptionHook = createSubscriptionHook(SubscriptionHookName.DeckSubscription, [
  {
    query: onLockDeck,
    eventType: 'onLockDeck',
    getInvalidateKeys: getDeckInvalidateKeys,
  },
  {
    query: onReleaseDeckLock,
    eventType: 'onReleaseDeckLock',
    getInvalidateKeys: getDeckInvalidateKeys,
  },
  {
    query: onUpdateDeck,
    eventType: 'onUpdateDeck',
    getInvalidateKeys: getDeckInvalidateKeys,
  },
]);

/**
 * Hook for subscribing to Deck-related events to support real-time Multi-User lockout feature, and track Deck Updates.
 * @param deckId - The unique identifier for the deck to subscribe to events
 * @param enabled - Whether the subscription should be made active
 */
export const useDeckSubscription = ({ deckId, enabled = false }: UseDeckSubscriptionParams): void => {
  deckSubscriptionHook({ enabled: enabled && !!deckId, scopeKey: deckId });
};
